import { noop } from 'lodash';
import React, { useContext, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import Divider from '@material-ui/core/Divider';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import SharedIcon from '@material-ui/icons/FolderSharedRounded';
import IEIcon from '@material-ui/icons/ImportExport';
import PersonIcon from '@material-ui/icons/Person';
import SaveIcon from '@material-ui/icons/Save';
import {
  ConfirmDialog,
  PromptContext,
  TableToolbarButton,
  UserContext,
  usePrompts,
} from '@eas/common-web';
import { CALENDAR_TABLE_VERSION } from '@modules/planner/work-order-table/work-order-table';
import { NamedSettingsContext } from '../context/named-settings-context';
import { ParsedTableNamedSettingsContext } from './table-named-settings-context';
import { CreateDialog } from './table-named-settings-create-dialog';
import { NamedSettingsItem } from './table-named-settings-item';
import {
  NamedSettingsButtonProps,
  ParsedTableNamedSettings,
} from './table-named-settings-types';

const MAX_TOPPED_COUNT = 99;

const isSavedItemValid = (item: ParsedTableNamedSettings): boolean => {
  if (item.settings.version === undefined) {
    return false;
  }
  if (item.settings.version !== CALENDAR_TABLE_VERSION) {
    return false;
  }
  return true;
};

export function NamedSettingsButton({
  disabled,
  onBeforeSelectSaved,
}: NamedSettingsButtonProps) {
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const { adminPermission } = useContext(NamedSettingsContext);
  const { hasPermission } = useContext(UserContext);
  const hasAdminPermission = adminPermission && hasPermission(adminPermission);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const showTestButton = searchParams.get('showTestButton') === 'true';

  const {
    openMenu,
    closeMenu,
    opened,
    savedItems,
    selectSaved,
    createSaved,
    updateSaved,
    deleteSaved,
    selectedItem,
    confirmDeleteDialog,
    createDialog,
    canEdit,
    updateSavedIsTop,
    importOldSettings,
    updateSettingsVersion,
    importProdNamedFiltersFromBackup,
  } = useContext(ParsedTableNamedSettingsContext);

  const intl = useIntl();

  const validItems = (savedItems ?? []).filter((item) =>
    isSavedItemValid(item)
  );
  const shared = validItems.filter((item) => item?.shared);
  const myFilters = validItems.filter((item) => !item?.shared);
  const toppedCount = myFilters.filter((item) => item?.settings?.isTop).length;

  const containsSettingsWithoutVersioning = savedItems
    .filter((s) => {
      if (!hasAdminPermission) {
        return !s.shared;
      }
      return true;
    })
    .some((item) => item?.settings?.version === undefined);

  const containsOldVersionSettings = savedItems
    .filter((s) => {
      if (!hasAdminPermission) {
        return !s.shared;
      }
      return true;
    })
    .some((item) => item?.settings?.version !== CALENDAR_TABLE_VERSION);

  const [showShared, setShowShared] = useState(false);
  usePrompts(
    [
      {
        key: 'PROMPT_CONFIRM_IMPORT_OLD_SETTINGS',
        dialogTitle: containsSettingsWithoutVersioning
          ? 'Import starších uložených nastavení'
          : 'Aktualizácia nastavení',
        dialogText: containsSettingsWithoutVersioning
          ? 'Pred pokračovaním je potrebné importovať Vaše staršie uložené nastavenia v dvoch krokoch.'
          : 'Pred pokračovaním je potrebné aktualizovať Vaše uložené nastavenia.',
      },
    ],
    [containsSettingsWithoutVersioning, containsOldVersionSettings]
  );
  const { testPrompt } = useContext(PromptContext);

  // TODO: remove after tested
  const devBtns = (
    <button onClick={() => importProdNamedFiltersFromBackup()}>import</button>
  );

  if (containsSettingsWithoutVersioning || containsOldVersionSettings) {
    return (
      <>
        {showTestButton && devBtns}
        <TableToolbarButton
          ref={anchorRef}
          disabled={disabled}
          label={
            <>
              <SaveIcon />
              <IEIcon />
            </>
          }
          onClick={async () => {
            testPrompt({
              key: 'PROMPT_CONFIRM_IMPORT_OLD_SETTINGS',
              submitCallback: async () => {
                if (containsSettingsWithoutVersioning) {
                  await importOldSettings?.();
                } else {
                  await updateSettingsVersion?.();
                }
              },
            });
          }}
          tooltip={'Uložené nastavenia (potrebná aktualizácia)'}
        />
      </>
    );
  }

  return (
    <>
      {showTestButton && devBtns}
      <TableToolbarButton
        ref={anchorRef}
        disabled={disabled}
        label={<SaveIcon />}
        onClick={openMenu}
        tooltip={intl.formatMessage({
          id: 'EAS_TABLE_SAVED_MENU_BTN',
          defaultMessage: 'Uložené nastavenia',
        })}
      />
      <Menu
        getContentAnchorEl={null}
        anchorEl={anchorRef.current}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={{
          disablePadding: true,
        }}
        open={opened}
        onClose={closeMenu}
      >
        <MenuItem
          // disabled={selectedItem !== null}
          onClick={() => {
            createDialog.current?.open();
            closeMenu();
          }}
        >
          <ListItemIcon>
            <AddCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <FormattedMessage
            id="EAS_TABLE_SAVED_MENU_CREATE"
            defaultMessage="Uložit jako..."
          />
        </MenuItem>
        <MenuItem
          disabled={selectedItem === null || !canEdit}
          onClick={updateSaved}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <FormattedMessage
            id="EAS_TABLE_SAVED_MENU_UPDATE"
            defaultMessage="Aktualizovat nastavení"
          />
        </MenuItem>
        <MenuItem
          disabled={selectedItem === null || !canEdit}
          onClick={() => {
            confirmDeleteDialog.current?.open();
            closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteForeverIcon fontSize="small" />
          </ListItemIcon>
          <FormattedMessage
            id="EAS_TABLE_SAVED_MENU_DELETE"
            defaultMessage="Smazat nastavení"
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setShowShared((p) => !p);
          }}
        >
          <ListItemIcon>
            <SharedIcon fontSize="small" />
          </ListItemIcon>
          Zdieľané filtre{' '}
          {showShared ? (
            <ArrowDropUpIcon fontSize="small" />
          ) : (
            <ArrowDropDownIcon fontSize="small" />
          )}
        </MenuItem>
        {showShared
          ? shared.map((saved, i) => (
              <NamedSettingsItem
                key={i}
                label={saved.name}
                selected={saved.id === selectedItem}
                closeMenu={closeMenu}
                onClick={() => {
                  onBeforeSelectSaved?.();
                  selectSaved(saved);
                }}
                isTop={saved.settings.isTop}
                onIsTopChange={(isTop) => updateSavedIsTop(saved.id, isTop)}
                showTopButton={!!hasAdminPermission}
              />
            ))
          : null}
        <Divider />
        <MenuItem>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Moje filtre{' '}
        </MenuItem>
        {myFilters.map((saved, i) => (
          <NamedSettingsItem
            key={i}
            label={saved.name}
            selected={saved.id === selectedItem}
            closeMenu={closeMenu}
            onClick={() => {
              onBeforeSelectSaved?.();
              selectSaved(saved);
            }}
            isTop={saved.settings.isTop}
            onIsTopChange={(isTop) => updateSavedIsTop(saved.id, isTop)}
            disableTopButton={
              toppedCount >= MAX_TOPPED_COUNT ? !saved.settings.isTop : false
            }
            showTopButton
          />
        ))}
        <Divider />
      </Menu>
      <ConfirmDialog
        ref={confirmDeleteDialog}
        onConfirm={() => deleteSaved(selectedItem!)}
        onCancel={noop}
        title={
          <FormattedMessage
            id="EAS_TABLE_SAVED_REMOVE_DIALOG_TITLE"
            defaultMessage="Varování"
          />
        }
        text={
          <FormattedMessage
            id="EAS_TABLE_SAVED_REMOVE_DIALOG_TEXT"
            defaultMessage="Opravdu chcete smazat uložené nastavení ?"
          />
        }
      />
      <CreateDialog onConfirm={createSaved} ref={createDialog} />
    </>
  );
}
